import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';
import { FlagsProvider } from 'react-feature-flags';
import styles from './App.module.scss';
import WishListsStore from './stores/WishListsStore';
import { EmailMeModal } from './components/EmailMeModal/EmailMeModal';
import EmailModalStore from './stores/EmailModalStore/EmailModalStore';
import { getFlagsFromCookies } from './helpers/featureFlags/featureFlags';
import { FEATURE_FLAGS } from './helpers/featureFlags/constants';
import Container from './container/Container';

interface AppProps {
    wishListStore?: WishListsStore;
    emailModalStoreProp?: EmailModalStore;
}

const emailModalStore = new EmailModalStore();

export const App: React.FC<AppProps> = observer(({ wishListStore = new WishListsStore(emailModalStore) }) => {
    const [store] = useState(wishListStore);
    const {
        wishLists,
        isCallingAPI,
        productExistsInWishLists,
        hasError,
        errorMessage,
        hasMoreThanOneWishlist,
        shouldWishListsBeExpanded,
        currentProductSkuId,
        currentProductMetadata,
        mapProductWishListForCurrentSku,
        getWishlists,
        handleWishListOnClick,
        checkIfWishListInURL,
        setupEventListeners,
    } = store;

    const flags: FeatureFlag[] = getFlagsFromCookies([FEATURE_FLAGS.NURSERY_PROTOTYPE, FEATURE_FLAGS.COLLAPSED_BUTTON]);

    useEffect(() => {
        setupEventListeners();
        if (navigator.userAgent !== 'ReactSnap') {
            // attempt network call only when not pre-rendering
            getWishlists().then(() => {
                checkIfWishListInURL();
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        mapProductWishListForCurrentSku();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wishLists, currentProductMetadata, currentProductSkuId]);

    const appClassNames = cx(styles.wishListsContainer, {
        [styles.expanded]: shouldWishListsBeExpanded,
    });

    return (
        <>
            <FlagsProvider value={flags}>
                <div
                    className={appClassNames}
                    data-testid="add-to-wish-list-app"
                    data-sku={currentProductSkuId}
                    data-product-metadata={currentProductMetadata}
                >
                    <Container
                        handleWishListOnClick={handleWishListOnClick}
                        productExistsInWishLists={productExistsInWishLists}
                        shouldWishListsBeExpanded={shouldWishListsBeExpanded}
                        wishLists={wishLists}
                        isCallingAPI={isCallingAPI}
                        hasError={hasError}
                        errorMessage={errorMessage}
                        hasMoreThanOneWishlist={hasMoreThanOneWishlist}
                    />
                </div>

                <EmailMeModal emailModalStore={emailModalStore} />
            </FlagsProvider>
        </>
    );
});
